import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Prf } from '../state-man/models/prf.model';
import { UiService } from './ui.service';
import { LoggerService } from './logger.service';
import { PrfPhoto } from '../state-man/models/prf-photo.model';
import { PhotoService } from './photo.service';
import { ApiError } from '../utils/api-error';

@Injectable({
  providedIn: 'root'
})
export class PrfService {

  constructor(
    private apiService: ApiService,
    private uiService: UiService
  ) { }

  public async getMine () : Promise<Prf[] | null> {   
    try {      
      const ret = await this.apiService.apiReturnsAny('get', 'prf/mine/')       
      const data = (ret && Array.isArray(ret)) ? Prf.buildMany(ret) : null
      if (!data) return null      
      return data      
    } catch (error) {
      LoggerService.log(error + 'prf/mine/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  // The signed-in technician's assigned truck, what a Usage PRF is sold from. null offline.
  public async getTruck() : Promise<{ truck_id: number | null, truck: string | null } | null> {
    try {
      const ret = await this.apiService.apiReturnsAny('get', 'prf/truck')
      return ret && typeof ret === 'object' ? { truck_id: ret.truck_id ?? null, truck: ret.truck ?? null } : null
    } catch (error) {
      LoggerService.log(error + 'prf/truck')
      return null
    }
  }

  public async addPrf(prf: Prf) : Promise<Prf | null> {
    try {
      const body = Prf.toApiPayload(prf)
      return await this.apiService.apiReturnsAny('post', 'prf/', body, 'application/json')
    } catch (error) {
      LoggerService.log(error + 'post prf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async updatePrf(prf: Prf) : Promise<Prf | null> {
    try {      
      const body = {data: Prf.toApiPayload(prf)}
      const ret = await this.apiService.apiReturnsAny('put', 'prf/' + prf.id, body, 'application/json')         
      return ret
    } catch (error) {
      LoggerService.log(error + 'put prf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async deletePrf(prf: Prf) : Promise<any> {
    try {
      const body = Prf.toApiPayload(prf)
      const ret = await this.apiService.apiReturnsAny('delete', 'prf/' + prf.id, body, 'application/json')                     
      return ret
    } catch (error) {    
      LoggerService.log(error + 'delete prf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')      
      return null
    }    
  }  
  
  // One line item photo. 'conflict' when the server won't take it (its line
  // is gone or the PRF is completed): the app drops it then. null when it
  // didn't get through and should be sent again later.
  public async addPhoto(line_uuid: string, photo: PrfPhoto) : Promise<PrfPhoto | 'conflict' | null> {
    try {
      const body = new FormData()
      const base64Image = photo.base64.split(';base64,').pop()
      const blob = PhotoService.b64toBlob(base64Image, 'image/jpeg')
      body.append('media_file', blob, photo.uuid + '.jpg')
      body.append('uuid', photo.uuid)
      body.append('line_uuid', line_uuid)
      const ret = await this.apiService.apiReturnsAny('post', 'prf/photos', body, 'multipart/form-data')
      return ret ? PrfPhoto.buildOne(ret) : null
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) return 'conflict'
      LoggerService.log(error + 'post prf/photos')
      return null
    }
  }

  // Full size, for a photo this device doesn't have. Online only.
  public async getPhoto(id: number) : Promise<{ photo_type: string, photo_data: string } | null> {
    try {
      return await this.apiService.apiReturnsAny('get', 'prf/photos/' + id)
    } catch (error) {
      LoggerService.log(error + 'get prf/photos/')
      return null
    }
  }

  public async deletePhoto(uuid: string) : Promise<boolean> {
    try {
      await this.apiService.apiReturnsAny('delete', 'prf/photos/' + uuid, {}, 'application/json')
      return true
    } catch (error) {
      // Not theirs to delete, or not a photo: nothing a retry would change.
      if (error instanceof ApiError && [400, 403, 404, 409].includes(error.status ?? 0)) return true
      LoggerService.log(error + 'delete prf/photos/')
      return false
    }
  }

}
